import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Users,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Plus,
  Search,
  Filter,
} from 'lucide-react';
import { formatarMoeda } from '@/lib/currency';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserMenu } from '@/components/common/UserMenu';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FiltrosClientes } from '@/components/clientes/FiltrosClientes';
import { ClienteFormDialog } from '@/components/cliente/ClienteFormDialog';
import {
  useClientesReais,
  type FiltrosClientesReais,
  type ClienteReal,
} from '@/hooks/useClientesReais';
import { useDebounce } from '@/hooks/useDebounce';
import { useFeatureTracking } from '@/hooks/useFeatureTracking';
import { PlanLimitBanner } from '@/components/common/PlanLimitBanner';

type SortKey =
  | 'codigo_cliente'
  | 'nome'
  | 'total_servicos'
  | 'valor_total_gasto'
  | 'ultimo_servico'
  | 'status_led'
  | 'origem'
  | 'criado_em';
type SortOrder = 'asc' | 'desc';

export default function Clientes() {
  useFeatureTracking('clientes');
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [filtros, setFiltros] = useState<FiltrosClientesReais>({});
  const [dialogAberto, setDialogAberto] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('codigo_cliente');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Busca local com debounce — feeds filtros.busca
  const [buscaLocal, setBuscaLocal] = useState('');
  const buscaDebounced = useDebounce(buscaLocal, 300);
  useEffect(() => {
    setFiltros((prev) =>
      prev.busca === (buscaDebounced || undefined)
        ? prev
        : { ...prev, busca: buscaDebounced || undefined },
    );
  }, [buscaDebounced]);

  const { data: clientes, isLoading, error } = useClientesReais(filtros);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const sortedClientes = useMemo(() => {
    return [...(clientes || [])].sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case 'codigo_cliente':
          comparison = (a.codigo_cliente || '').localeCompare(b.codigo_cliente || '');
          break;
        case 'nome':
          comparison = (a.nome || '').localeCompare(b.nome || '');
          break;
        case 'total_servicos':
          comparison = a.total_servicos - b.total_servicos;
          break;
        case 'valor_total_gasto':
          comparison = a.valor_total_gasto - b.valor_total_gasto;
          break;
        case 'ultimo_servico': {
          const dataA = a.ultimo_servico ? new Date(a.ultimo_servico).getTime() : 0;
          const dataB = b.ultimo_servico ? new Date(b.ultimo_servico).getTime() : 0;
          comparison = dataA - dataB;
          break;
        }
        case 'status_led': {
          const order = { lead: 0, ativo: 1, inativo: 2, pendente: 3 };
          comparison = order[a.status_led] - order[b.status_led];
          break;
        }
        case 'origem':
          comparison = (a.origem || '').localeCompare(b.origem || '');
          break;
        case 'criado_em': {
          const a1 = a.criado_em ? new Date(a.criado_em).getTime() : 0;
          const b1 = b.criado_em ? new Date(b.criado_em).getTime() : 0;
          comparison = a1 - b1;
          break;
        }
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [clientes, sortKey, sortOrder]);

  const handleClienteClick = (cliente: ClienteReal) => {
    navigate(`/cliente-potential/${cliente.id}`);
  };

  const totalAll = clientes?.length || 0;
  const totalVisible = sortedClientes.length;

  const SortIcon = ({ sortKeyValue }: { sortKeyValue: SortKey }) => {
    if (sortKey !== sortKeyValue) {
      return <ArrowUpDown className="inline ml-1 h-3 w-3 opacity-50" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp className="inline ml-1 h-3 w-3" />
    ) : (
      <ChevronDown className="inline ml-1 h-3 w-3" />
    );
  };

  return (
    <MainLayout>
      <div className="p-6 flex flex-col gap-5">
        <PageHeader
          titulo={t('clients.title')}
          badge={
            <span className="ow-badge ow-badge--outline">
              {totalAll} {t('clients.title').toLowerCase()}
            </span>
          }
          acoes={
            <>
              <Popover open={filtrosAbertos} onOpenChange={setFiltrosAbertos}>
                <PopoverTrigger asChild>
                  <button type="button" className="ow-btn ow-btn--outline ow-btn--sm">
                    <Filter size={14} />
                    {t('common.filters')}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-96 max-h-[80vh] overflow-y-auto">
                  <FiltrosClientes filtros={filtros} onFiltrosChange={setFiltros} />
                </PopoverContent>
              </Popover>
              <button
                type="button"
                className="ow-btn ow-btn--primary ow-btn--sm"
                onClick={() => setDialogAberto(true)}
              >
                <Plus size={14} />
                {t('clients.addClient')}
              </button>
              <UserMenu />
            </>
          }
        />

        <PlanLimitBanner tipo="clientes" label="clientes" />

        <div className="dst-toolbar">
          <label className="dst-toolbar__search">
            <Search size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
            <input
              type="text"
              placeholder={t('clients.searchPlaceholder')}
              value={buscaLocal}
              onChange={(e) => setBuscaLocal(e.target.value)}
            />
          </label>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12 text-destructive">
            {t('common.errorLoadingData')}
          </div>
        ) : sortedClientes.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('common.noResults')}</p>
            <p className="text-sm mt-1">
              {filtros.busca || filtros.status?.length || filtros.tipo?.length
                ? t('clients.filters.clear')
                : t('clients.subtitle')}
            </p>
          </div>
        ) : (
          <div className="tbl-card">
            <table className="tbl">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('codigo_cliente')}
                    style={{ cursor: 'pointer', width: 110 }}
                  >
                    {t('clients.table.code')}
                    <SortIcon sortKeyValue="codigo_cliente" />
                  </th>
                  <th onClick={() => handleSort('nome')} style={{ cursor: 'pointer' }}>
                    {t('common.name')}
                    <SortIcon sortKeyValue="nome" />
                  </th>
                  <th
                    className="num"
                    onClick={() => handleSort('total_servicos')}
                    style={{ cursor: 'pointer', width: 130 }}
                  >
                    {t('clients.table.totalServices')}
                    <SortIcon sortKeyValue="total_servicos" />
                  </th>
                  <th
                    className="num"
                    onClick={() => handleSort('valor_total_gasto')}
                    style={{ cursor: 'pointer', width: 150 }}
                  >
                    {t('clients.table.totalSpent')}
                    <SortIcon sortKeyValue="valor_total_gasto" />
                  </th>
                  <th
                    onClick={() => handleSort('ultimo_servico')}
                    style={{ cursor: 'pointer', width: 160 }}
                  >
                    {t('common.phone')}
                  </th>
                  <th
                    className="hidden md:table-cell"
                    onClick={() => handleSort('origem')}
                    style={{ cursor: 'pointer', width: 150 }}
                  >
                    {t('clients.table.origin')}
                    <SortIcon sortKeyValue="origem" />
                  </th>
                  <th
                    className="hidden lg:table-cell"
                    onClick={() => handleSort('criado_em')}
                    style={{ cursor: 'pointer', width: 150 }}
                  >
                    {t('clients.table.firstContact')}
                    <SortIcon sortKeyValue="criado_em" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedClientes.map((cliente) => {
                  const inicial =
                    cliente.nome?.charAt(0)?.toUpperCase() || '?';
                  return (
                    <tr key={cliente.id} onClick={() => handleClienteClick(cliente)}>
                      <td className="mono">{cliente.codigo_cliente || '-'}</td>
                      <td>
                        <div className="tbl-name">
                          <span className="tbl-avt" aria-hidden>
                            {cliente.avatar_url ? <img src={cliente.avatar_url} alt="" /> : inicial}
                          </span>
                          <span className="tbl-name__nm">
                            {cliente.nome || t('common.noName')}
                          </span>
                        </div>
                      </td>
                      <td className="num mono">
                        {cliente.total_servicos > 0 ? cliente.total_servicos : '-'}
                      </td>
                      <td className="num t-currency">
                        {cliente.valor_total_gasto > 0
                          ? formatarMoeda(cliente.valor_total_gasto)
                          : '-'}
                      </td>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {cliente.telefone}
                      </td>
                      <td className="hidden md:table-cell">
                        <span className="text-muted-foreground">
                          {cliente.origem || '-'}
                        </span>
                      </td>
                      <td
                        className="hidden lg:table-cell text-muted-foreground"
                        style={{ fontSize: 12 }}
                      >
                        {cliente.criado_em
                          ? format(new Date(cliente.criado_em), 'dd/MM/yyyy', {
                              locale: ptBR,
                            })
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && (
          <p className="text-center text-xs font-medium text-muted-foreground">
            {totalVisible} {t('common.of', 'de')} {totalAll}
          </p>
        )}

        <ClienteFormDialog open={dialogAberto} onOpenChange={setDialogAberto} />
      </div>
    </MainLayout>
  );
}
